import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "../ui/card";
import {
  Bell,
  Clock,
  CheckCircle2,
  AlertCircle,
  Info,
  Trash2,
  CheckCheck,
} from "lucide-react";
import { Button } from "../ui/button";

interface Notification {
  id: string;
  title: string;
  description: string;
  time: string;
  type: "info" | "success" | "warning" | "error";
  read: boolean;
}

const initialNotifications: Notification[] = [
  {
    id: "1",
    title: "New Client Registered",
    description: "Client 'Xero Systems' added.",
    time: "2 mins ago",
    type: "success",
    read: false,
  },
  {
    id: "2",
    title: "System Maintenance",
    description: "Maintenance at 02:00 AM.",
    time: "1 hour ago",
    type: "warning",
    read: false,
  },
  {
    id: "3",
    title: "Subscription Expiring",
    description: "Expires in 3 days.",
    time: "5 hours ago",
    type: "error",
    read: true,
  },
];

// Preserved gradients for active filter state visual flair, 
// but you can change these to bg-primary if you want strict theming
const filterStyles = {
  all: "from-purple-500 to-pink-500",
  unread: "from-red-500 to-orange-500",
  system: "from-emerald-500 to-green-400",
  security: "from-blue-500 to-indigo-500",
};

export function Notifications() {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [filter, setFilter] = useState<
    "all" | "unread" | "system" | "security"
  >("all");

  const getIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const filteredNotifications = useMemo(() => {
    if (filter === "unread") return notifications.filter(n => !n.read);
    if (filter === "system") return notifications.filter(n => n.type === "warning");
    if (filter === "security") return notifications.filter(n => n.type === "error");
    return notifications;
  }, [notifications, filter]);

  const markAllAsRead = () =>
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));

  const clearAll = () => setNotifications([]);

  const deleteOne = (id: string) =>
    setNotifications(prev => prev.filter(n => n.id !== id));

  const markAsRead = (id: string) =>
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );

  return (
    <div className="min-h-screen bg-background p-6 text-foreground font-sans">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex gap-3 items-center text-foreground">
              <Bell className="h-8 w-8 text-muted-foreground" />
              Notifications
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time system activity feed
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={markAllAsRead} variant="secondary" className="bg-secondary hover:bg-secondary/80">
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark all
            </Button>
            <Button onClick={clearAll} variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </motion.div>

        {/* FILTERS */}
        <div className="flex gap-3">
          {(["all", "unread", "system", "security"] as const).map((f) => (
            <motion.div key={f} whileTap={{ scale: 0.95 }}>
              <div
                onClick={() => setFilter(f)}
                className={`
                  relative px-5 py-2 rounded-full cursor-pointer text-sm font-semibold
                  border transition-all duration-300
                  ${filter === f
                    ? `bg-gradient-to-r ${filterStyles[f]} text-white border-transparent shadow-md`
                    : "bg-muted text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground"
                  }
                `}
              >
                {f.toUpperCase()}
              </div>
            </motion.div>
          ))}
        </div>

        {/* LIST */}
        <Card className="rounded-3xl bg-card border-border shadow-sm">
          <CardContent className="p-4 space-y-3">
            <AnimatePresence>
              {filteredNotifications.map((notif) => (
                <motion.div
                  key={notif.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => markAsRead(notif.id)}
                  className="
                    group relative p-5 rounded-2xl
                    bg-card border border-border
                    hover:border-primary/50 hover:bg-accent/50
                    transition-all duration-300 cursor-pointer shadow-sm
                  "
                >
                  <div className="flex gap-4 relative z-10">
                    {/* ICON */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="p-3 rounded-xl bg-muted text-muted-foreground flex items-center justify-center h-12 w-12"
                    >
                      {getIcon(notif.type)}
                    </motion.div>

                    {/* CONTENT */}
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                          {notif.title}
                          {!notif.read && (
                            <span className="h-2 w-2 rounded-full bg-destructive inline-block animate-pulse" />
                          )}
                        </h3>
                        <span className="text-xs text-muted-foreground flex gap-1 items-center">
                          <Clock className="h-3 w-3" />
                          {notif.time}
                        </span>
                      </div>

                      <p className="text-sm text-muted-foreground mt-1">
                        {notif.description}
                      </p>
                    </div>

                    {/* DELETE */}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOne(notif.id);
                      }}
                      size="icon"
                      variant="ghost"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {filteredNotifications.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-muted-foreground py-10"
              >
                No notifications 🚀
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}